import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, User, Wallet, ScrollText, ClipboardList, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatTRY, formatDate, todayISO } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MoneyInput from "@/components/MoneyInput";

export default function CariDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [cari, setCari] = useState(null);
  const [balance, setBalance] = useState(null);
  const [txs, setTxs] = useState([]);
  const [checks, setChecks] = useState([]);
  const [hakedis, setHakedis] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dialog, setDialog] = useState(null); // "payment" | "invoice" | "hakedis"
  const [form, setForm] = useState({});

  const load = async () => {
    const [{ data: c }, { data: b }, { data: t }, { data: ck }, { data: h }, { data: p }] = await Promise.all([
      api.get(`/cariler/${id}`),
      api.get(`/cariler/${id}/balance`),
      api.get(`/transactions?cari_id=${id}`),
      api.get(`/checks?cari_id=${id}`),
      api.get(`/hakedis?cari_id=${id}`),
      api.get(`/projects`),
    ]);
    setCari(c); setBalance(b); setTxs(t); setChecks(ck); setHakedis(h); setProjects(p);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openDialog = (type) => {
    if (type === "payment") setForm({ amount: 0, description: "Ödeme", date: todayISO() });
    else if (type === "invoice") setForm({ amount: 0, description: "", date: todayISO() });
    else if (type === "hakedis") setForm({ amount: 0, description: "", period: todayISO().slice(0, 7), project_id: projects[0]?.id || "" });
    setDialog(type);
  };

  const submit = async () => {
    try {
      if (dialog === "payment") {
        await api.post("/transactions", {
          type: "gider",
          amount: Number(form.amount),
          category: "Cari Ödemesi",
          date: form.date,
          description: form.description,
          cari_id: id,
        });
        toast.success("Ödeme kaydedildi");
      } else if (dialog === "invoice") {
        await api.post("/transactions", {
          type: "gider",
          amount: Number(form.amount),
          category: "Fatura",
          date: form.date,
          description: form.description,
          cari_id: id,
        });
        toast.success("Fatura kaydedildi");
      } else if (dialog === "hakedis") {
        await api.post("/hakedis", {
          amount: Number(form.amount),
          description: form.description,
          period: form.period,
          project_id: form.project_id,
          cari_id: id,
        });
        toast.success("Hakediş kaydedildi");
      }
      setDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cari || !balance) return <div className="text-muted-foreground text-sm">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="cari-detail-page">
      <button
        onClick={() => navigate("/cariler")}
        className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground font-semibold flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> Cariler
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-md bg-secondary flex items-center justify-center">
            <User className="w-7 h-7 text-brand" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{cari.name}</h1>
            <div className="text-xs text-muted-foreground mt-1">
              {cari.type === "taseron" ? "Taşeron" : cari.type === "tedarikci" ? "Tedarikçi" : "Müşteri"}
              {cari.tax_office && ` · ${cari.tax_office} · ${cari.tax_no}`}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            data-testid="cari-export-btn"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/cari/${cari.id}/excel`, "_blank", "noopener")}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Ekstre İndir
          </Button>
          {hasPerm("muhasebe.create") && <Button data-testid="quick-payment" onClick={() => openDialog("payment")} variant="outline"><Wallet className="w-4 h-4 mr-2" />Ödeme Yap</Button>}
          {hasPerm("muhasebe.create") && <Button data-testid="quick-invoice" onClick={() => openDialog("invoice")} variant="outline"><ScrollText className="w-4 h-4 mr-2" />Fatura Ekle</Button>}
          {cari.type !== "musteri" && hasPerm("hakedis.create") && (
            <Button data-testid="quick-hakedis" onClick={() => openDialog("hakedis")}><ClipboardList className="w-4 h-4 mr-2" />Hakediş Ekle</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`p-4 shadow-none rounded-md border-l-4 ${balance.bakiye >= 0 ? "border-l-[hsl(var(--danger))]" : "border-l-[hsl(var(--success))]"}`}>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Bakiye</div>
          <div className={`font-heading text-xl font-bold tabular-nums ${balance.bakiye >= 0 ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--success))]"}`}>
            {formatTRY(balance.bakiye)}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            {balance.bakiye >= 0 ? "Cariye borçluyuz" : "Cari bize borçlu"}
          </div>
        </Card>
        <Card className="p-4 shadow-none rounded-md">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Toplam Gider</div>
          <div className="font-heading text-xl font-bold tabular-nums">{formatTRY(balance.gider)}</div>
        </Card>
        <Card className="p-4 shadow-none rounded-md">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Hakediş</div>
          <div className="font-heading text-xl font-bold tabular-nums">{formatTRY(balance.hakedis)}</div>
        </Card>
        <Card className="p-4 shadow-none rounded-md">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Bekleyen Çekler</div>
          <div className="font-heading text-xl font-bold tabular-nums">{formatTRY(balance.verilen_bekleyen + balance.alinan_bekleyen)}</div>
        </Card>
      </div>

      <Tabs defaultValue="tx">
        <TabsList>
          <TabsTrigger value="tx" data-testid="cari-tab-tx">İşlemler ({txs.length})</TabsTrigger>
          <TabsTrigger value="checks" data-testid="cari-tab-checks">Çekler ({checks.length})</TabsTrigger>
          <TabsTrigger value="hakedis" data-testid="cari-tab-hakedis">Hakedişler ({hakedis.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="tx" className="mt-4">
          <Card className="shadow-none rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Tarih</TableHead><TableHead>Tür</TableHead><TableHead>Kategori</TableHead>
                  <TableHead>Açıklama</TableHead><TableHead>Kaydeden</TableHead><TableHead className="text-right">Tutar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txs.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-xs tabular-nums">{formatDate(t.date)}</TableCell>
                    <TableCell>{t.type === "gelir" ? "Gelir" : "Gider"}</TableCell>
                    <TableCell className="text-sm">{t.category}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.description}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{t.created_by_name || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatTRY(t.amount)}</TableCell>
                  </TableRow>
                ))}
                {txs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">İşlem yok</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="checks" className="mt-4">
          <Card className="shadow-none rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Vade</TableHead><TableHead>Tür</TableHead><TableHead>Açıklama</TableHead>
                  <TableHead>Durum</TableHead><TableHead className="text-right">Tutar</TableHead>
                  <TableHead className="text-right w-24">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checks.map((c) => {
                  const overdue = c.status === "bekliyor" && c.due_date < new Date().toISOString().slice(0, 10);
                  const statusTone = c.status === "odendi"
                    ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"
                    : c.status === "karsiliksiz"
                    ? "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]"
                    : overdue
                    ? "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]"
                    : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]";
                  const statusLabel = c.status === "odendi" ? "Ödendi" : c.status === "karsiliksiz" ? "Karşılıksız" : overdue ? "Gecikmiş" : "Bekliyor";
                  return (
                    <TableRow key={c.id} data-testid={`cari-check-row-${c.id}`}>
                      <TableCell className="text-xs tabular-nums">{formatDate(c.due_date)}</TableCell>
                      <TableCell className="text-xs">{c.type === "alinan" ? "Alınan" : "Verilen"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{c.description || "-"}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${statusTone}`}>{statusLabel}</span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{formatTRY(c.amount)}</TableCell>
                      <TableCell className="text-right">
                        {c.status === "bekliyor" && hasPerm("cekler.edit") && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`mark-paid-cari-${c.id}`}
                            onClick={async () => {
                              try {
                                await api.patch(`/checks/${c.id}/status?status=odendi`);
                                toast.success("Taksit ödendi olarak işaretlendi");
                                load();
                              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                            }}
                            className="h-7 text-xs"
                          >
                            Ödendi
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {checks.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Çek yok</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="hakedis" className="mt-4">
          <Card className="shadow-none rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Dönem</TableHead><TableHead>Proje</TableHead>
                  <TableHead>Açıklama</TableHead><TableHead>Kaydeden</TableHead><TableHead className="text-right">Tutar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hakedis.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-sm tabular-nums">{h.period}</TableCell>
                    <TableCell className="text-sm">{projects.find((p) => p.id === h.project_id)?.name || "-"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{h.description}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{h.created_by_name || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatTRY(h.amount)}</TableCell>
                  </TableRow>
                ))}
                {hakedis.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Hakediş yok</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dialog === "payment" ? "Ödeme Yap" : dialog === "invoice" ? "Fatura / Alacak Ekle" : "Hakediş Ekle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Tutar</Label>
              <MoneyInput data-testid="quick-amount" value={form.amount || 0} onChange={(n) => setForm({ ...form, amount: n })} />
            </div>
            {dialog === "hakedis" ? (
              <>
                <div className="space-y-2"><Label>Dönem (YYYY-MM)</Label><Input value={form.period || ""} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
                <div className="space-y-2"><Label>Proje</Label>
                  <Select value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div className="space-y-2"><Label>Tarih</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            )}
            <div className="space-y-2"><Label>Açıklama</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>İptal</Button>
            <Button data-testid="quick-save" onClick={submit} disabled={!form.amount}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
